import React, { useEffect } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import {
  Button,
  Box,
  Grid,
  IconButton,
  Typography,
} from '@mui/material'
import { DeleteOutline, EditOutlined } from '@mui/icons-material';
import InputField from 'components/Input-Field/input-field';
import SelectField from 'components/Select-Field/select-field';
import { DataGrid } from '@mui/x-data-grid';
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog';

const MaintenanceForm = ({ action,
  maintenanceRecord,
  maintenanceList,
  saveFormHandler,
  updateFormHandler,
  fnOnEditHandler,
  fnOnDeleteHandler,
}) => {
  const [selectedRow, setSelectedRow] = React.useState(null);
  const [categories, setCategories] = React.useState([]);
  const [isConfirmationDialogOpen, setConfirmationDialogOpen] = React.useState(false);
  const columns = [
    { field: 'id', headerName: 'Id' },
    { field: 'category', headerName: 'Category', flex: 1 },
    {
      field: 'description',
      headerName: 'Service',
      flex: 1,
    },
    { field: 'serviceDate', headerName: 'Service Date', flex: 1 },
    { field: 'amount', headerName: 'Cost', flex: 1 },
    { field: 'mileage', headerName: 'Miles', flex: 1 },
    { field: 'serviceCenter', headerName: 'Service Center', flex: 1 },
    { field: 'volume', headerName: 'Volume', flex: 1 },
    {
      field: 'action',
      headerName: 'Action',
      flex: 1,
      renderCell: (params) => {
        return (
          <>
            <IconButton size='small' onClick={() => editRow(params.row)}>
              <EditOutlined fontSize='small' color='primary' />
            </IconButton>
            <IconButton size='small' onClick={() => deleteRow(params.row)}>
              <DeleteOutline fontSize='small' color='secondary' />
            </IconButton>
          </>
        );
      },
    },
  ];

  const formik = useFormik({
    initialValues: { ...maintenanceRecord },
    enableReinitialize: true,
    validationSchema: Yup.object().shape({
      category: Yup.string().min(2).max(100).required('Service Category is required.'),
      description: Yup.string().required('Service Description is required'),
      amount: Yup.number().min(0).required('Amount is required.'),
      mileage: Yup.number().min(0).required('Mileage Odometer Reading is required.'),
    }),

    onSubmit: values => {
      const trimmed_data = {};
      Object.keys(values).forEach(key => trimmed_data[key] = typeof (values[key]) === 'string' ? values[key].trim() : values[key]);
      action === 'Save' ? saveFormHandler(trimmed_data) : updateFormHandler(trimmed_data);
    },
  });

  const editRow = (row) => {
    const maintenance = { ...row };
    formik.initialValues = { ...maintenance };
    fnOnEditHandler(maintenance);
  };

  const deleteRow = (row) => {
    setSelectedRow(row);
    setConfirmationDialogOpen(true);
  };

  const processDelete = () => {
    fnOnDeleteHandler(selectedRow);
    setConfirmationDialogOpen(false);
  };

  const handleSelectAddComboChange = (evt, newValue) => {
    if (newValue === null) return;
    if (typeof newValue === 'string') {
      formik.handleChange(evt);
      formik.setFieldValue('category', newValue);
    } else if (newValue && newValue.inputValue) {
      // Create a new value from the user input
      formik.handleChange(evt);
      formik.setFieldValue('category', newValue.inputValue);
    } else {
      formik.handleChange(evt);
      formik.setFieldValue('category', newValue.name);
    }
  }

  const renderForm = () => {
    return (
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={{xs: 4, sm: 4}}>
            <SelectField
              label='Select Category'
              name='category'
              select
              value={formik.values.category}
              variant='outlined'
              fullWidth
              error={Boolean(formik.touched.category && formik.errors.category)}
              helperText={formik.touched.category && formik.errors.category}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              options={['SERVICE', 'FUEL', 'INSPECTION', 'PAYMENT', 'INSURANCE', 'OTHERS']}
            />
          </Grid>
          <Grid size={{xs: 6, sm: 6}}>
            <InputField
              label='Description'
              name='description'
              value={formik.values.description}
              variant='outlined'
              fullWidth
              error={Boolean(formik.touched.description && formik.errors.description)}
              helperText={formik.touched.description && formik.errors.description}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
            />
          </Grid>

          <Grid size={{xs: 2, sm: 2}}>
            <InputField
              label='Amount'
              name='amount'
              type='number'
              value={formik.values.amount}
              variant='outlined'
              fullWidth
              error={Boolean(formik.touched.amount && formik.errors.amount)}
              helperText={formik.touched.amount && formik.errors.amount}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
            />
          </Grid>
          {/* ROW 2 */}
          <Grid size={{xs: 4}}>
            <InputField
              label='Vendor'
              name='serviceCenter'
              value={formik.values.serviceCenter}
              type='text'
              variant='outlined'
              fullWidth
              error={Boolean(formik.touched.serviceCenter && formik.errors.serviceCenter)}
              helperText={formik.touched.serviceCenter && formik.errors.serviceCenter}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
            />
          </Grid>          
          <Grid size={{xs: 3}}>
            <InputField
              label='Service Date'
              name='serviceDate'
              value={formik.values.serviceDate}
              type='date'
              variant='outlined'
              fullWidth
              error={Boolean(formik.touched.serviceDate && formik.errors.serviceDate)}
              helperText={formik.touched.serviceDate && formik.errors.serviceDate}
              slotProps={{ inputLabel: { shrink: true } }}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid size={{xs: 3}}>
            <InputField
              label='Mileage'
              name='mileage'
              value={formik.values.mileage}
              type='text'
              variant='outlined'
              fullWidth
              error={Boolean(formik.touched.mileage && formik.errors.mileage)}
              helperText={formik.touched.mileage && formik.errors.mileage}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
            />
          </Grid>
          {formik.values.category === 'FUEL' &&
            <Grid size={{xs: 3}}>
              <InputField
                label='Fuel Volume'
                name='volume'
                value={formik.values.volume}
                type='number'
                variant='outlined'
                fullWidth
                error={Boolean(formik.touched.volume && formik.errors.volume)}
                helperText={formik.touched.volume && formik.errors.volume}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              />
            </Grid>
          }
        </Grid>
        <Box
          display='flex'
          justifyContent='flex-end'
          p={2}>
          {action === 'Save' ?
            <Button
              type='submit'
              variant='contained'
              color='primary'
            >
              Save Record
            </Button> :
            <Button
              type='submit'
              variant='contained'
              color='primary'
            >
              Update Record
            </Button>
          }
        </Box>
      </form>
    );
  };

  return (
    <React.Fragment>
      <Box
        m='10px 0 0 0'
      >
        {renderForm()}
      </Box>
      <Box
        m='20px 0 0 0'
        height='40vh'
      >
        <DataGrid
          rows={maintenanceList || []}
          columns={columns}
          rowHeight={40}
          disableSelectionOnClick
        />
      </Box>
      {selectedRow &&
        <ConfirmationDialog
          open={isConfirmationDialogOpen}
          title={'Delete Record'}
          handleConfirm={() => processDelete()}
          handleCancel={() => setConfirmationDialogOpen(false)}
          handleCloseFunc={() => setConfirmationDialogOpen(false)}
        >
          <Typography variant='subtitle2'>{`Do you want to delete Service Record: ${selectedRow && selectedRow.id} ?`}</Typography>
        </ConfirmationDialog>}
    </React.Fragment>
  );
};

export default MaintenanceForm;